import { useState } from "react"
import { useTranslation } from "react-i18next"
import { AUTH_CODE_TYPE_ENUM } from "@antipartido-inc/contracts-ifaces"
import { Button, TextField, Checkbox } from "@cividao/components"
import styles from "./index.module.css"
import { DomainError } from "../../../../domain/_kernel/DomainError"

interface CredentialsStepProps {
  onComplete: (email: string) => void
}

export function CredentialsStep({ onComplete }: CredentialsStepProps) {
  const { t } = useTranslation("auth")
  const [email, setEmail] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [privacyAccepted, setPrivacyAccepted] = useState(false)

  const isFormValid = !!email && termsAccepted && privacyAccepted

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
  }

  const handleSubmit = () => {
    if (!isFormValid) return

    setIsLoading(true)
    setError("")

    window.domain.SendAuthCodeAuthUseCase.execute({
      email,
      type: AUTH_CODE_TYPE_ENUM.REGISTER,
    })
      .then(() => {
        onComplete(email)
      })
      .catch((err: unknown) => {
        const errorCode = err instanceof DomainError ? err.code : "unknown"
        const errorMessage = String(t(`errors.${errorCode}`, { defaultValue: t("errors.unknown") }))
        setError(errorMessage)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  return (
    <div className={styles.form}>
      <TextField
        label={t("shared.email.label")}
        type="email"
        value={email}
        onChange={handleEmailChange}
        placeholder={t("shared.email.placeholder")}
      />

      <Checkbox
        checked={termsAccepted}
        onChange={(e) => setTermsAccepted(e.target.checked)}
        label={
          <span>
            {t("register.acceptTerms.prefix")}{" "}
            <a href="/terms" rel="noopener noreferrer" className={styles.checkboxLink}>
              {t("register.acceptTerms.link")}
            </a>
          </span>
        }
      />

      <Checkbox
        checked={privacyAccepted}
        onChange={(e) => setPrivacyAccepted(e.target.checked)}
        label={
          <span>
            {t("register.acceptPrivacy.prefix")}{" "}
            <a href="/privacy" rel="noopener noreferrer" className={styles.checkboxLink}>
              {t("register.acceptPrivacy.link")}
            </a>
          </span>
        }
      />

      {error && <div className={styles.error}>{error}</div>}

      <Button onClick={handleSubmit} isDisabled={!isFormValid} isLoading={isLoading} isFull>
        {t("shared.button")}
      </Button>
    </div>
  )
}
